import {
  flattenItems,
  PREFILL_BASELINE_MEMBER,
  readValue,
  repeatingSections,
  RESERVED_RESPONSE_MEMBERS,
  seedRows,
  type AnswerMap,
  type FormDefinition,
  type FormItem,
  type ItemType,
} from "@njbsoft/form-logic";

/** A copy of the draft's recorded prefill baseline: its string entries by link_id. */
function recordedBaseline(answers: Record<string, unknown>): Record<string, string> {
  const member = answers[PREFILL_BASELINE_MEMBER];
  const out: Record<string, string> = {};
  if (typeof member !== "object" || member === null || Array.isArray(member)) return out;
  for (const [linkId, read] of Object.entries(member)) {
    if (typeof read === "string") out[linkId] = read;
  }
  return out;
}

/**
 * Builds the once-per-mount seed patch for a draft's read-source answers: the
 * pinned facility_ref answer (the assignment's facility id), each
 * prefill-mode read_source item's local read-context value, and every
 * seeded repeating section's rows from the bundle's resolved collections.
 *
 * Returns ONLY the keys to ADD (plus the updated baseline member when a
 * write-back seed changed it) — never a full answer map:
 * - existing draft answers are never overwritten (a resumed draft's edits win,
 *   and a given assignment's facility never changes on mobile, so there is no
 *   re-fill on facility change) — this applies to repeating-section answers
 *   too: a section the tester has already touched (or a prior seed already
 *   populated) is never re-patched;
 * - display items are never seeded — they render live from the read context,
 *   the client seals their values at submit, and the server re-injects the
 *   facility's display labels at receipt, never a `device.*` value;
 * - the context carries strings, and each is coerced as the item's type through
 *   the shared `readValue` — the coercion the seal applies to a display read
 *   and to the write-back baseline — so a number or boolean prefill is the JSON
 *   number or boolean the receipt admits. A missing, empty or malformed value
 *   is skipped, leaving the answer absent rather than seeding "" or a string
 *   a number item cannot hold;
 * - a repeating section with no `seed.read_source` is never seeded — it starts
 *   as a blank, tester-entered grid;
 * - every `write_back` item it seeds records the RAW read string it was seeded
 *   from under the draft's reserved `PREFILL_BASELINE_MEMBER`, keyed by its
 *   link_id (the patch then carries the whole updated member). A key the draft
 *   already holds is not re-seeded and keeps its recorded entry, so a draft
 *   resumed after a newer pull still seals the read its answer came from and
 *   an untouched prefill never writes over the office's correction. An absent
 *   write-back answer whose read has no value records nothing and forgets any
 *   entry it held.
 *
 * Row seeding delegates to the shared `@njbsoft/form-logic` `seedRows` — the
 * same coercion logic the web composer's facility cascade uses, so mobile and
 * web share one implementation of the collection-cell-to-answer mapping.
 *
 * Idempotent by construction: re-running with the already-seeded answers yields
 * an empty patch, so a focus re-run is a no-op.
 */
export function buildPrefillPatch(
  definition: FormDefinition,
  answers: Record<string, unknown>,
  facilityId: string,
  readContext: Record<string, string>,
  collections: Record<string, Array<Record<string, string>>> = {},
): Record<string, unknown> {
  const patch: Record<string, unknown> = {};
  const { ordered } = flattenItems(definition);
  const recorded = recordedBaseline(answers);
  const seededReads: Record<string, string> = {};
  const forgotten = new Set<string>();
  for (const item of ordered) {
    if (answers[item.link_id] !== undefined) continue;
    if (item.type === "facility_ref") {
      patch[item.link_id] = facilityId;
      continue;
    }
    if (item.read_source && (item.read_mode ?? "prefill") === "prefill") {
      const read = readContext[item.read_source];
      const v = readValue(item.type, read);
      if (v !== undefined) patch[item.link_id] = v;
      if (item.write_back !== true) continue;
      if (v !== undefined && read !== undefined) seededReads[item.link_id] = read;
      else if (Object.hasOwn(recorded, item.link_id)) forgotten.add(item.link_id);
    }
  }
  if (Object.keys(seededReads).length > 0 || forgotten.size > 0) {
    const kept = Object.entries(recorded).filter(([linkId]) => !forgotten.has(linkId));
    patch[PREFILL_BASELINE_MEMBER] = { ...Object.fromEntries(kept), ...seededReads };
  }
  for (const section of repeatingSections(definition)) {
    if (answers[section.link_id] !== undefined) continue;
    const seedKey = section.seed?.read_source;
    if (seedKey === undefined) continue;
    patch[section.link_id] = seedRows(section, collections[seedKey] ?? []);
  }
  return patch;
}

export interface PrefillSeedPlan {
  answers: AnswerMap;
  /** False when nothing changed OR the record is sealed — the seed must never reach disk for a sealed assignment. */
  persist: boolean;
}

// A resubmission retakes its evidence: the rejected set's blobs die with it,
// so a carried attachment id would dangle. These item types answer afresh on
// every cure.
const RETAKEN_ITEM_TYPES: ReadonlySet<ItemType> = new Set(["document", "signature"]);

// The `responses` keys that are not answers.
const RESERVED_MEMBERS: ReadonlySet<string> = new Set(RESERVED_RESPONSE_MEMBERS);

/** Drops the retaken cells from one carried repeating-section row. */
function stripRetakenCells(row: unknown, retaken: ReadonlySet<string>): unknown {
  if (typeof row !== "object" || row === null) return row;
  const out: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(row)) {
    if (!retaken.has(key)) out[key] = value;
  }
  return out;
}

/** The predecessor's sealed prefill baseline: the raw read string per write-back link id. */
function sealedBaseline(priorResponses: AnswerMap): ReadonlyMap<string, string> {
  const member = priorResponses[PREFILL_BASELINE_MEMBER];
  const reads = new Map<string, string>();
  if (typeof member !== "object" || member === null || Array.isArray(member)) return reads;
  for (const [linkId, read] of Object.entries(member)) {
    if (typeof read === "string") reads.set(linkId, read);
  }
  return reads;
}

/**
 * Whether a write-back item's prior answer is its untouched prefill: it equals,
 * as the item's own type, the read the predecessor sealed as its baseline
 * (`12` against `"12"`, `true` against `"true"`). An item with no baseline
 * entry, or one whose entry does not parse as its type, was never an untouched
 * prefill, so its answer is the tester's.
 */
function isUntouchedPrefill(
  item: FormItem,
  value: unknown,
  baseline: ReadonlyMap<string, string>,
): boolean {
  if (item.write_back !== true) return false;
  const read = baseline.get(item.link_id);
  if (read === undefined) return false;
  const prefilled = readValue(item.type, read);
  return prefilled !== undefined && prefilled === value;
}

/**
 * What a rejected predecessor's `responses` may seed into its cure's draft:
 * every answer EXCEPT the retaken evidence types, whose fields start empty
 * for retake. Read-source and display values carry too — they re-derive at
 * seal, so seeding them is presentation, not truth. Repeating-section answers
 * carry row-for-row with the retaken cells dropped from each row; a key the
 * definition does not know carries verbatim (the seal prunes unknown answers).
 *
 * Two things never carry. The reserved members — the prefill baseline and the
 * condition reads — are the predecessor's seal, not answers; the cure seals its
 * own. A write-back item's answer that still equals its sealed baseline is an
 * untouched prefill: dropping it leaves the key absent, so `buildPrefillPatch`
 * re-seeds it from the CURRENT read and records that read as the cure draft's
 * baseline, which the cure's seal carries — a stale value can never write over
 * a correction the office made between the rejection and the cure. A changed
 * write-back answer carries like any other, with no baseline entry, so it
 * writes.
 *
 * The CALLER owns the merge, ABSENT KEYS ONLY — the draft's own answers win
 * over the carried values, the same precedence `buildPrefillPatch` holds over
 * the result.
 */
export function carriedCureAnswers(
  definition: FormDefinition,
  priorResponses: AnswerMap,
): AnswerMap {
  const { byId } = flattenItems(definition);
  const baseline = sealedBaseline(priorResponses);
  const retakenRowCells = new Map<string, ReadonlySet<string>>();
  for (const section of repeatingSections(definition)) {
    const cells = new Set(
      section.items.filter((item) => RETAKEN_ITEM_TYPES.has(item.type)).map((item) => item.link_id),
    );
    if (cells.size > 0) retakenRowCells.set(section.link_id, cells);
  }
  const carried: AnswerMap = {};
  for (const [key, value] of Object.entries(priorResponses)) {
    if (RESERVED_MEMBERS.has(key)) continue;
    const item = byId.get(key);
    if (item !== undefined && RETAKEN_ITEM_TYPES.has(item.type)) continue;
    if (item !== undefined && isUntouchedPrefill(item, value, baseline)) continue;
    const retaken = retakenRowCells.get(key);
    if (retaken === undefined) {
      carried[key] = value;
      continue;
    }
    carried[key] = Array.isArray(value)
      ? value.map((row) => stripRetakenCells(row, retaken))
      : value;
  }
  return carried;
}

/**
 * Applies the cure carry and the facility read-context seed to a draft, ABSENT
 * KEYS ONLY (the carry via `carry`, the rest via `buildPrefillPatch`), and
 * decides whether the result may be persisted. The disk save is refused for a
 * SEALED assignment: gating on the draft's own locked flag is not enough,
 * because post-acknowledgement cleanup DELETES the draft and a later save
 * would INSERT a fresh UNLOCKED draft full of seeded answers for an
 * already-submitted assignment — the seed must be able to reach neither the
 * screen's regulatory record nor disk. The carry counts toward `persist` on
 * its own: a carry that adds keys must reach disk even when the facility
 * patch is empty (the patch may have persisted on an earlier pass).
 */
export function planPrefillSeed(args: {
  definition: FormDefinition;
  draftAnswers: AnswerMap;
  facilityId: string;
  readContext: Record<string, string>;
  collections: Parameters<typeof buildPrefillPatch>[4];
  sealed: boolean;
  /**
   * A resubmission's carried prior answers, already exclusion-filtered by
   * `carriedCureAnswers`. Optional — a plain start passes nothing and the
   * plan is exactly the facility seed.
   */
  carry?: AnswerMap;
}): PrefillSeedPlan {
  const carryPatch: AnswerMap = {};
  for (const [key, value] of Object.entries(args.carry ?? {})) {
    if (args.draftAnswers[key] === undefined) carryPatch[key] = value;
  }
  const seeded: AnswerMap =
    Object.keys(carryPatch).length > 0
      ? { ...args.draftAnswers, ...carryPatch }
      : args.draftAnswers;
  const patch = buildPrefillPatch(
    args.definition,
    seeded,
    args.facilityId,
    args.readContext,
    args.collections,
  );
  const changed = Object.keys(carryPatch).length > 0 || Object.keys(patch).length > 0;
  if (!changed) return { answers: seeded, persist: false };
  return { answers: { ...seeded, ...patch }, persist: !args.sealed };
}
